import {
  Box,
  Button,
  Link,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { exists } from '@cnx-saas/utils-fe';

import { Loading, SelectCountry } from '@cnx-saas/common-fe';
import {
  CountryCodes,
  useGetMe_Query,
  useGetMyTenant_Query,
  UserLanguages,
  UserType,
  useUpdateMe_Mutation,
  useUpdateMyTenant_Mutation,
} from '@cnx-saas/common/generated';
import { useSnackbar } from '@cnx-saas/snackbar';
import { SelectLanguage } from '../form-fields';
import { getAccountFormSchema } from './account.schema';
import { FormContainer, SectionTitle } from './account.style';
import { AccountFormValuesKeycloak } from './account.type';
import { LogoUploader } from './LogoUploader';
import { useChangePassword } from './useChangePassword';

export function AccountKeycloak() {
  const { t, i18n } = useTranslation();
  const { addAlert } = useSnackbar();
  const formRef = createRef<FormikProps<AccountFormValuesKeycloak>>();

  const { openChangePasswordFormDialog } = useChangePassword();

  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);
  const [initialData, setInitialData] = React.useState({
    firstName: '',
    lastName: '',
    language: UserLanguages.De,
    email: '',
    address: {
      street: '',
      city: '',
      zipCode: '',
      country: CountryCodes.De,
    },
    taxId: '',
    cargoboardApiKey: '',
  });

  const getMe = useGetMe_Query();
  const getMyTenant = useGetMyTenant_Query();
  const [updateMe] = useUpdateMe_Mutation();
  const [updateMyTenant] = useUpdateMyTenant_Mutation();

  const validationSchema = getAccountFormSchema(t);

  useEffect(() => {
    if (getMe.data?.getMe && getMyTenant.data?.getMyTenant) {
      const account = getMe.data?.getMe;
      const tenant = getMyTenant.data?.getMyTenant;

      setInitialData({
        firstName: account.firstName,
        lastName: account.lastName,
        language: account.language as UserLanguages,
        email: account.email,
        address: {
          street: tenant.street ?? '',
          city: tenant.city ?? '',
          zipCode: tenant.zipCode ?? '',
          country: (tenant.country as CountryCodes) ?? CountryCodes.De,
        },
        taxId: tenant.taxId ?? '',
        cargoboardApiKey: tenant.cargoboardApiKey ?? '',
      });
    }
  }, [getMe.data, getMyTenant.data]);

  function onClickSubmit() {
    if (exists(formRef.current)) {
      const { handleSubmit } = formRef.current;

      handleSubmit();
    }
  }

  async function onSubmit(values: AccountFormValuesKeycloak) {
    setSubmitButtonDisabled(true);

    const updateMePromise = updateMe({
      variables: {
        details: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          language: values.language,
        },
      },
    });

    const updateMyTenantPromise = updateMyTenant({
      variables: {
        details: {
          street: values.address.street,
          city: values.address.city,
          zipCode: values.address.zipCode,
          country: values.address.country,
          taxId: values.taxId,
          cargoboardApiKey: values.cargoboardApiKey,
        },
      },
    });

    (async () => {
      try {
        await Promise.all([updateMePromise, updateMyTenantPromise]);
        await i18n.changeLanguage(
          values.language === UserLanguages.Gb
            ? 'en'
            : values.language.toLocaleLowerCase(),
        );
        addAlert({ children: t('account.save.success'), type: 'success' });
      } catch (e) {
        console.log(e);
        addAlert({ children: t('account.save.error'), type: 'error' });
      } finally {
        setSubmitButtonDisabled(false);
      }
    })();
  }

  if (getMe.loading || getMyTenant.loading) {
    return <Loading />;
  }

  return (
    <FormContainer>
      <Formik
        initialValues={initialData}
        enableReinitialize
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={validationSchema}
      >
        {({ values, errors, touched, handleChange }) => {
          return (
            <Box
              sx={{
                backgroundColor: (theme) => theme.palette.background.default,
                padding: 4,
                paddingTop: 0,
                marginBottom: 0,
                borderRadius: 8,
              }}
            >
              <Form>
                <AccountGrid>
                  <SectionTitle gridArea="userSection">
                    {t('account.formLabel.userSection')}
                  </SectionTitle>
                  <Field
                    name="firstName"
                    label={t('account.formLabel.firstName')}
                    error={
                      exists(errors.firstName) && exists(touched.firstName)
                    }
                    helperText={errors.firstName ?? undefined}
                    value={values.firstName}
                    onChange={handleChange}
                    as={TextField}
                    required
                    style={{ gridArea: 'firstName' }}
                  />
                  <Field
                    name="lastName"
                    error={exists(errors.lastName) && exists(touched.lastName)}
                    helperText={errors.lastName ?? undefined}
                    label={t('account.formLabel.lastName')}
                    value={values.lastName}
                    onChange={handleChange}
                    as={TextField}
                    required
                    style={{ gridArea: 'lastName' }}
                  />
                  <Field
                    name="email"
                    error={exists(errors.email) && exists(touched.email)}
                    helperText={errors.email ?? undefined}
                    label={t('account.formLabel.email')}
                    value={values.email}
                    onChange={handleChange}
                    as={TextField}
                    disabled
                    required
                    style={{ gridArea: 'email' }}
                  />
                  <Field
                    name="language"
                    error={exists(errors.language) && exists(touched.language)}
                    errorMessage={errors.language ?? undefined}
                    label={t('account.formLabel.language')}
                    as={SelectLanguage}
                    required
                    style={{ gridArea: 'language' }}
                  />
                  <Link
                    component="button"
                    variant="body2"
                    underline="none"
                    onClick={(e) => {
                      e.preventDefault();
                      openChangePasswordFormDialog();
                    }}
                    gridArea="changePassword"
                  >
                    <Typography fontWeight={600} mr={1} mb={4} width={150}>
                      {t('userManagement.form.changePassword', {
                        defaultValue: 'Change Password',
                      })}
                    </Typography>
                  </Link>
                  {getMe.data?.getMe?.userType === UserType.TeamAdmin && (
                    <>
                      <SectionTitle gridArea="companySection">
                        {t('account.formLabel.companySection')}
                      </SectionTitle>
                      <Field
                        name="address.street"
                        error={
                          exists(errors.address?.street) &&
                          exists(touched.address?.street)
                        }
                        helperText={errors.address?.street ?? undefined}
                        label={t('account.formLabel.address.street')}
                        value={values.address.street}
                        onChange={handleChange}
                        as={TextField}
                        required
                        style={{ gridArea: 'street' }}
                      />
                      <Field
                        name="address.city"
                        error={
                          exists(errors.address?.city) &&
                          exists(touched.address?.city)
                        }
                        helperText={errors.address?.city ?? undefined}
                        label={t('account.formLabel.address.city')}
                        value={values.address.city}
                        onChange={handleChange}
                        as={TextField}
                        required
                        style={{ gridArea: 'city' }}
                      />
                      <Field
                        name="address.zipCode"
                        error={
                          exists(errors.address?.zipCode) &&
                          exists(touched.address?.zipCode)
                        }
                        helperText={errors.address?.zipCode ?? undefined}
                        label={t('account.formLabel.address.zipCode')}
                        value={values.address.zipCode}
                        onChange={handleChange}
                        as={TextField}
                        required
                        style={{ gridArea: 'zipCode' }}
                      />
                      <Field
                        name="address.country"
                        error={
                          exists(errors.address?.country) &&
                          exists(touched.address?.country)
                        }
                        errorMessage={errors.address?.country ?? undefined}
                        label={t('account.formLabel.address.country')}
                        as={SelectCountry}
                        required
                        style={{ gridArea: 'country' }}
                      />
                      <Field
                        name="taxId"
                        error={exists(errors.taxId) && exists(touched.taxId)}
                        helperText={errors.taxId ?? undefined}
                        label={t('account.formLabel.taxId')}
                        value={values.taxId}
                        onChange={handleChange}
                        as={TextField}
                        required
                        style={{ gridArea: 'taxId' }}
                      />
                      <Field
                        name="cargoboardApiKey"
                        label={t('account.formLabel.cargoboardApiKey')}
                        value={values.cargoboardApiKey}
                        onChange={handleChange}
                        as={TextField}
                        style={{ gridArea: 'cargoboardApiKey' }}
                      />
                      <Box gridArea="logoUpload">
                        <LogoUploader />
                      </Box>
                    </>
                  )}
                  <Button
                    type="submit"
                    onClick={onClickSubmit}
                    disabled={submitButtonDisabled}
                    sx={{ width: '100px', gridArea: 'save', mb: 4 }}
                  >
                    {t('account.formLabel.submit')}
                  </Button>
                </AccountGrid>
              </Form>
            </Box>
          );
        }}
      </Formik>
    </FormContainer>
  );
}

const AccountGrid = styled(Box)({
  display: 'grid',
  grid: `
  "userSection          userSection"
  "firstName            lastName"
  "email                language"
  "changePassword       ."
  "companySection       ."
  "street               logoUpload"
  "city                 logoUpload"
  "zipCode              logoUpload"
  "country              logoUpload"
  "taxId                logoUpload"
  "cargoboardApiKey     logoUpload"
  "save                 ."
  / 1fr 1fr`,
  gap: '16px',
  placeItems: 'start stretch',
});
